import {
  doc,
  setDoc,
  updateDoc,
  getDoc,
  arrayUnion,
  addDoc,
  collection,
  writeBatch,
} from "firebase/firestore";
import { db, auth } from "@/Utils/firebase";
import {
  getAvatarFromLocalStorage,
  convertToInches,
  getOSVersion,
  getBrowserVersion,
  getDeviceInfo,
  getGender,
} from "@/Utils";
import { isProdEnv } from "@/Utils/constants";
import { logError } from "@/Utils/logger";
import {
  getAvatarSharingData,
  setSharedScanId,
} from "@/Utils/avatarSharingStorage";
import { queryClient } from "@/Utils/queryClient";
import { upsertScanInList } from "@/Utils/scansList";

export class ScanModel {
  private id: string;

  constructor(id: string) {
    this.id = id;
  }

  public static async create(
    id: string,
    opts: {
      storePhotosEnabled?: boolean;
      segmentationVersion?: string | null;
      // Caller-supplied uid from the finalize-time `ensureAuthedUser()` guard.
      // Takes precedence over `auth.currentUser?.uid` because on the iOS Safari
      // / in-app WKWebView contexts that motivate the guard, `auth.currentUser`
      // can be null at finalize even though a valid anonymous user was signed
      // in (Failure mode G). See ScanUploader.runFinalizeScan and
      // HERE_BE_DRAGONS.md § "Auth flow".
      uid?: string;
    } = {},
  ) {
    const deviceInfo = getDeviceInfo();
    let scanID = localStorage.getItem("scanID");

    // Get sharing data from storage utility
    const sharingData = getAvatarSharingData();
    const clientId = sharingData.clientId || "";
    const sessionId = sharingData.sessionId || "";
    const appVersion = process.env.REACT_APP_VERSION;
    const user = auth.currentUser;
    const newAvatar = getAvatarFromLocalStorage();
    const email = newAvatar?.email || "";
    const name = `${newAvatar?.firstName} ${newAvatar?.lastName || ""}`.trim();
    let height = newAvatar?.height
      ? convertToInches(newAvatar.height).toFixed(2)
      : "";
    const userHeight = parseFloat(height);
    const gender = newAvatar?.gender || "";
    const weight = newAvatar?.weight ? parseFloat(newAvatar.weight) : 0;
    const age = newAvatar?.age ? parseInt(newAvatar.age) : 0;

    // Optional Avatar-info fields collected only on the Sisley white-label
    // (see NewAvatarScreen + the Body Scan Procedures doc), so Sisley can
    // connect them to orders.
    //
    // These live under the `customerInfo` MAP, not at the top level. That
    // namespacing is the D3 contract agreed with the backend — see the
    // dashboard repo's `docs/pwa-sisley-d3-handover.md` §2. It buys two things:
    // customer-entered values can never collide with a system field on
    // scans/{id}, and the sibling `garmentMeasures` key (server-computed, see
    // SISLEY_WHITELABEL.md) stays trivially blocklistable in firestore.rules
    // the way `pipelineStatus` is — a client must not be able to forge measures
    // Sisley connects to real orders.
    //
    // Only fields the user actually filled in are included, and the map is
    // omitted entirely when empty — so non-Sisley scan docs stay byte-identical.
    // Any downstream consumer (bodyscanner backend / dashboard order-connection
    // logic) is a lockstep dependency — see SISLEY_WHITELABEL.md.
    const customerInfo: Record<string, string> = {};
    const addCustomerInfo = (key: string, value?: string) => {
      const trimmed = (value || "").trim();
      if (trimmed) customerInfo[key] = trimmed;
    };
    addCustomerInfo("accountType", newAvatar?.accountType);
    addCustomerInfo("companyName", newAvatar?.companyName);
    addCustomerInfo("handedness", newAvatar?.handedness);
    addCustomerInfo("waistband", newAvatar?.waistband);
    addCustomerInfo("headCircumference", newAvatar?.headCircumference);
    addCustomerInfo("handMeasurement", newAvatar?.handMeasurement);
    addCustomerInfo("fingerMeasurement", newAvatar?.fingerMeasurement);
    addCustomerInfo("notes", newAvatar?.notes);
    const hasCustomerInfo = Object.keys(customerInfo).length > 0;

    let pwaVersion = appVersion || "";
    let osVersion = getOSVersion();

    // Prefer the caller-supplied uid (from ensureAuthedUser's credential) and
    // fall back to auth.currentUser only when it isn't passed. Never read
    // auth.currentUser as the primary source here — it can be null at finalize
    // on the affected iOS contexts even with a valid signed-in user.
    let userId = opts.uid ?? user?.uid;
    // validate the required data
    if (!userId) {
      throw new Error("User ID is required!");
    }

    if (!userHeight || !gender || !weight || !age) {
      throw new Error("Required data is missing!");
    }

    // validate data types
    if (isNaN(userHeight) || isNaN(weight) || isNaN(age)) {
      throw new Error("Invalid data types!");
    }

    const scanData = {
      email: email.trim(),
      name: name,
      height: userHeight,
      pose: "T",
      gender: getGender(gender),
      timestamp: Math.floor(Date.now() / 1000),
      storagePath: `scans/${userId}/${scanID}`,
      pwaVersion: pwaVersion,
      osVersion: osVersion,
      browserVersion: getBrowserVersion(),
      hardwareModel: deviceInfo.model,
      pipelineStatus: "PROCESSING",
      userId: userId,
      clientId: clientId,
      sessionId: sessionId,
      weight: !isNaN(weight) && weight !== 0 ? weight : null,
      age: !isNaN(age) && age !== 0 ? age : null,
      hasFullBodyScan: true,
      hasTorsoScan: false,
      locale: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      storePhotosEnabled: opts.storePhotosEnabled === true,
      // Same checkbox, two fields, on purpose.
      //
      // storePhotosEnabled has existed since 2026-05 and means "photos are
      // retained; TrueToForm may use them for accuracy and troubleshooting".
      // sharePhotosEnabled is newer and means "the accounts this scan is
      // shared with may VIEW AND EXPORT the photos" — the dashboard's photo
      // export gates on it, and on nothing else.
      //
      // The split is temporal, not semantic: only builds carrying the
      // "Attach photos to my scan…" wording write the second field, so scans
      // predating it keep retention without becoming exportable. Do NOT
      // "simplify" the dashboard to read storePhotosEnabled instead — that
      // silently makes every pre-existing scan exportable, which is the exact
      // outcome this exists to prevent. See the dashboard's
      // docs/scan-photo-export.md.
      sharePhotosEnabled: opts.storePhotosEnabled === true,
      ...(opts.segmentationVersion
        ? { segmentationVersion: opts.segmentationVersion }
        : {}),
      ...(hasCustomerInfo ? { customerInfo } : {}),
    };

    try {
      // Atomic: create scans/{id} AND mark upload_processing/{id}.resolved=true
      // in the same batch. `resolved` is the terminal flag the orphan-cleanup
      // sweep (cleanupOrphanScans) checks; setting it here ensures committed
      // scans never appear in the sweep, so no per-doc existence check is
      // needed server-side. merge:true protects against the rare case where
      // upload_processing/{id} doesn't yet exist (e.g. slow segmentation).
      const batch = writeBatch(db);
      const docRef = doc(db, "scans", id);
      const uploadProcessingRef = doc(db, "upload_processing", id);
      batch.set(docRef, scanData);
      batch.set(
        uploadProcessingRef,
        { resolved: true, resolved_timestamp: Math.floor(Date.now() / 1000) },
        { merge: true },
      );
      await batch.commit();
      // The avatars list is read once and kept in the app's react-query cache
      // (src/Utils/scansList.ts), so the scan just committed goes into it now:
      // by the time the user reaches the list, success has removed the scanID
      // behind the "Submitting Scans ..." card. Only after the commit, never
      // before it, and never able to fail a scan that is already written.
      try {
        upsertScanInList(queryClient, userId, { id, ...scanData });
      } catch (err) {
        logError("[ScanModel] could not add the new scan to the cached list", {
          message: (err as Error)?.message ?? null,
        });
      }
      try {
        // update the session if it exists
        if (sessionId) {
          const sessionRef = doc(db, "widget_sessions", sessionId);
          await updateDoc(sessionRef, {
            activeScan: {
              id: scanID,
              name: name,
              timestamp: new Date(),
              userId: userId,
            },
            progress: "UPLOADED",
          });
        }
      } catch (err) {
        logError("Error updating session:", err as Error);
      }

      // reset the localStorage values
      localStorage.removeItem("scanID");
      localStorage.removeItem("timestamp");
      // BUG-0012: the share is NOT cleared here any more. It used to be, 2 s
      // after every successful submit, which sent a redo after a failed avatar
      // to nobody. It now stays until the scan it was used for is approved —
      // decided when the next scan form opens (src/Utils/sharedScanApproval.ts)
      // — or ends with the 2-hour window or the shopper's Cancel. Record which
      // scan used it, so that decision has something to check.
      if (clientId) setSharedScanId(id);
      return new ScanModel(id);
    } catch (err) {
      // if the error was because scan already exists
      if ((err as { code: string }).code === "firestore/already-exists") {
        throw new Error("Scan Exists!");
      }
      throw err;
    }
  }

  public static async createDebugDocument(
    errorMsg: string
  ): Promise<string | undefined> {
    if (isProdEnv) return "dev only";
    try {
      const deviceInfo = getDeviceInfo();
      const user = auth.currentUser;
      if (!user) return "user not found";

      const scanData = {
        timestamp: Math.floor(Date.now() / 1000),
        pwaVersion: process.env.REACT_APP_VERSION,
        osVersion: getOSVersion(),
        browserVersion: getBrowserVersion(),
        hardwareModel: deviceInfo.model,
        pipelineStatus: "CANCELLED",
        userId: user?.uid,
        hasFullBodyScan: true,
        locale: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        rotationError: {
          errorMsg,
          errorDesc: "", // no need to add desc for internal debugging
        },
      };

      const docRef = collection(db, `scans_debug`);

      const doc = await addDoc(docRef, scanData);
      // update the doc to have the storage path
      await updateDoc(doc, {
        storagePath: `scans_debug/${user?.uid}/${doc.id}`,
      });
      return doc.id;
    } catch (e) {
      logError("Error adding document: ", e as Error);
    }
  }

  async get() {
    const docRef = doc(db, "scans", this.id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return null;
    }
  }

  async shareScan(clientId: string) {
    // validate the clientId
    if (!clientId) {
      throw new Error("Client ID is required!");
    }
    if (clientId.length !== 6) {
      throw new Error("Client ID must be 6 characters long!");
    }
    // add the clientId to the clientIds lists
    const docRef = doc(db, "scans", this.id);
    await updateDoc(docRef, {
      clientIds: arrayUnion(clientId),
    });
  }
}
